import { captureNetworkError } from '../services/error-tracking.service';
import { finalDoPedido } from './redact-link-credentials';

export interface PurchaseEventData {
  event: string;
  transaction_id: string;
  value: number;
  currency: string;
  coupon?: string;
  items: Array<{
    item_id: string;
    item_name: string;
    price: number;
    quantity: number;
  }>;
  payment_method: 'card' | 'pix';
  plan_id?: string;
  plan_name?: string;
}

/**
 * Retry curto (#133): a conversão (gclid → Google Ads) era fetch único
 * fire-and-forget — um soluço do n8n perdia a atribuição sem deixar rastro.
 * Duas re-tentativas com backoff curto cabem na janela antes do redirect
 * (2s no cartão) e `keepalive` segura o último envio mesmo se a página
 * trocar. Esgotou → vai pro error-tracking (que já tem retry/batch próprio).
 */
const RETRY_DELAYS_MS = [500, 1500];
const ATTEMPT_TIMEOUT_MS = 4000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function postOnce(url: string, body: string): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ATTEMPT_TIMEOUT_MS);
  try {
    return await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true, // Garante envio mesmo se página redirecionar
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

async function postWithRetry(url: string, body: string, transactionId: string): Promise<void> {
  let lastError: unknown = null;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    if (attempt > 0) await sleep(RETRY_DELAYS_MS[attempt - 1]);
    try {
      const response = await postOnce(url, body);
      if (response.ok) return;
      // 4xx não vai melhorar re-tentando; 5xx pode.
      lastError = new Error(`n8n respondeu ${response.status} ${response.statusText}`);
      if (response.status < 500) break;
    } catch (err) {
      lastError = err;
    }
  }
  captureNetworkError(lastError, url, 'POST', {
    component: 'n8n-webhook',
    action: 'purchase_conversion_failed',
    additionalInfo: {
      transactionId: transacaoParaLog(transactionId),
      attempts: RETRY_DELAYS_MS.length + 1,
    },
  });
}

/**
 * #626: no PIX o `transaction_id` é o nº do pedido AppMax, que abre
 * `/pix/<pedido>` (QR e nome do titular). No log de erro vai só o final
 * (`…432`). No cartão é o id do pagamento ou `pre-auth-<ts>`, que não abrem
 * nada e seguem inteiros.
 */
function transacaoParaLog(transactionId: string): string | undefined {
  return /^\d+$/.test(String(transactionId)) ? finalDoPedido(transactionId) : transactionId;
}

// Envia dados do purchase para n8n (não bloqueia o fluxo de compra)
export const sendPurchaseToN8n = (data: PurchaseEventData) => {
  try {
    const url = import.meta.env.VITE_N8N_WEBHOOK_URL as string | undefined;
    if (!url) return; // dev sem webhook configurado

    // Captura gclid e gbraid da URL
    const params = new URLSearchParams(window.location.search);
    const gclid = params.get('gclid') || null;
    const gbraid = params.get('gbraid') || null;

    const payload = {
      ...data,
      gclid,
      gbraid,
      timestamp: new Date().toISOString(),
      source: 'checkout',
      url: window.location.href,
    };

    // Fire-and-forget do ponto de vista de quem chama; internamente re-tenta.
    void postWithRetry(url, JSON.stringify(payload), data.transaction_id);
  } catch (err) {
    captureNetworkError(err, 'n8n-webhook', 'POST', {
      component: 'n8n-webhook',
      action: 'purchase_conversion_build_failed',
    });
  }
};
